import { List } from '@mui/material';
import React, { useMemo, useState, useEffect } from 'react'
import { getVariantText } from '../../../../../../utils/helper';
import { useSelector } from 'react-redux';
import RenderNodeValues from './RenderNodeValues';

const RenderProperty = ({properties, onListUpdate}) => {
    const [propertyList, setPropertyList] = useState(properties);
    const state = useSelector((state) => state.buildingProfileTable);
    const scope = useSelector((state) => state.container.scope);
    const openAsset = useSelector((state) => state.openAsset);

    // Helper function to check if property matches any of the given keywords
    const propertyMatchesKeywords = (property, keywords) => {
      return keywords.some(keyword =>
        property.nodeName?.toUpperCase().includes(keyword.toUpperCase()) ||
        property.labelTranslation?.some(label => 
          label.title?.toUpperCase().includes(keyword.toUpperCase())
        )
      );
    };

    // Helper function to get subAssetType from openAsset or properties
    const getSubAssetType = () => {
      if (openAsset?.subAssetType) return openAsset.subAssetType;
      
      const firstPropertyWithData = properties.find(prop => 
        prop.nodeValue?.length > 0 && prop.nodeValue[0].subAsset
      );
      return firstPropertyWithData?.nodeValue[0].subAsset.subAssetType;
    };

    // Define specific property node names to show based on subAssetType
    const getFilteredPropertyNodeNames = (subAssetType) => {
      const propertyMappings = {
        'Wohnen': [
          'ANZAHL DER WOHNUNGEN', 'ANZAHL WOHNUNGEN', 'NUMBER OF FLATS', 'WOHNUNGSANZAHL'
        ],
        'Logistik': [
          'TRAGFÄHIGKEIT BODEN', 'FLOOR LOAD CAPACITY', 'BODENBELASTUNG',
          'LICHTE HÖHE', 'CLEAR HEIGHT', 'DECKENHÖHE', 'RAUMHÖHE'
        ],
        'Hotel': [
          'SHORT NAME', 'KURZNAME',
          'LOCATION', 'STANDORT', 'LAGE',
          'CONSTRUCTION YEAR', 'BAUJAHR', 'ERRICHTUNGSJAHR',
          'TYPE OF USE', 'NUTZUNGSART', 'VERWENDUNG',
          'NUMBER OF ROOMS', 'ANZAHL ZIMMER', 'ZIMMERANZAHL',
          'ANZAHL GÄSTEZIMMER', 'NUMBER OF GUESTROOMS', 'GÄSTEZIMMER',
          'NUMBER OF BEDS', 'ANZAHL BETTEN', 'BETTENANZAHL',
          'SUSTAINABILITY CERTIFICATE', 'NACHHALTIGKEITSZERTIFIKAT', 'ZERTIFIKAT'
        ]
      };
      
      return propertyMappings[subAssetType] || [];
    };

    // Check if a property should be shown based on subAssetType
    const shouldShowProperty = (property, subAssetType) => {
      // Define the specific properties that should be filtered based on subAssetType
      const restrictedProperties = [
        'ANZAHL DER WOHNUNGEN', 'ANZAHL WOHNUNGEN', 'NUMBER OF FLATS', 'WOHNUNGSANZAHL',
        'TRAGFÄHIGKEIT BODEN', 'FLOOR LOAD CAPACITY', 'BODENBELASTUNG',
        'LICHTE HÖHE', 'CLEAR HEIGHT', 'DECKENHÖHE', 'RAUMHÖHE',
        'SHORT NAME', 'KURZNAME',
        'NUMBER OF ROOMS', 'ANZAHL ZIMMER', 'ZIMMERANZAHL',
        'ANZAHL GÄSTEZIMMER', 'NUMBER OF GUESTROOMS', 'GÄSTEZIMMER',
        'NUMBER OF BEDS', 'ANZAHL BETTEN', 'BETTENANZAHL',
        'SUSTAINABILITY CERTIFICATE', 'NACHHALTIGKEITSZERTIFIKAT', 'ZERTIFIKAT'
      ];

      // If it's not a restricted property or no subAssetType, always show it
      if (!propertyMatchesKeywords(property, restrictedProperties) || !subAssetType) {
        return true;
      }

      // For restricted properties, check if they match the current subAssetType
      return propertyMatchesKeywords(property, getFilteredPropertyNodeNames(subAssetType));
    };

    // Filter properties based on subAssetType from openAsset or from property data
    useEffect(() => {
      const subAssetType = getSubAssetType();
      const filteredProperties = properties.filter(property => 
        shouldShowProperty(property, subAssetType)
      );
      setPropertyList(filteredProperties);
    }, [properties, openAsset?.subAssetType]);

    // Custom ordering for specific properties
    const getCustomOrder = (property, subAssetType) => {
      if (!subAssetType) return property.order || 999;

      // Define order arrays for different building types
      const orderMappings = {
        'Hotel': [
          'SHORT NAME', 'KURZNAME',
          'LOCATION', 'STANDORT', 'LAGE',
          'CONSTRUCTION YEAR', 'BAUJAHR', 'ERRICHTUNGSJAHR',
          'TYPE OF USE', 'NUTZUNGSART', 'VERWENDUNG',
          'NUMBER OF ROOMS', 'ANZAHL ZIMMER', 'ZIMMERANZAHL',
          'NUMBER OF GUESTROOMS', 'ANZAHL GÄSTEZIMMER', 'GÄSTEZIMMER',
          'NUMBER OF BEDS', 'ANZAHL BETTEN', 'BETTENANZAHL',
          'SUSTAINABILITY CERTIFICATE', 'NACHHALTIGKEITSZERTIFIKAT', 'ZERTIFIKAT'
        ],
        'Wohnen': [
          'SHORT NAME', 'KURZNAME',
          'LOCATION', 'STANDORT', 'LAGE',
          'CONSTRUCTION YEAR', 'BAUJAHR', 'ERRICHTUNGSJAHR',
          'TYPE OF USE', 'NUTZUNGSART', 'VERWENDUNG',
          'NUMBER OF FLATS', 'ANZAHL DER WOHNUNGEN', 'ANZAHL WOHNUNGEN', 'WOHNUNGSANZAHL'
        ]
      };

      const orderArray = orderMappings[subAssetType];
      if (!orderArray) return property.order || 999;

      // Find matching property in the ordered list
      const orderIndex = orderArray.findIndex(orderKey =>
        property.nodeName?.toUpperCase().includes(orderKey.toUpperCase()) ||
        property.labelTranslation?.some(label => 
          label.title?.toUpperCase().includes(orderKey.toUpperCase())
        )
      );

      return orderIndex !== -1 ? orderIndex + 1 : (property.order || 999);
    };

    const updatePropertyList = (item, nodeValues) => {
      let temp = [...propertyList];
      const index = temp.findIndex((t) => t.nodeIdent === item.nodeIdent);
      if(index !== -1) {
        temp[index] = {...temp[index], nodeValue: nodeValues, isUpdated: true};
      }
      setPropertyList(temp);
      onListUpdate(temp);
    }

    const sortedItemList = useMemo(() => {
      const subAssetType = getSubAssetType();

      // Apply custom sorting based on subAssetType
      if (subAssetType === 'Hotel' || subAssetType === 'Wohnen') {
        // For Hotel and Wohnen: Use the strict custom ordering
        return propertyList.toSorted((a, b) => {
          const orderA = getCustomOrder(a, subAssetType);
          const orderB = getCustomOrder(b, subAssetType);
          
          // If custom order differs, use it; otherwise fall back to original order
          return orderA !== orderB 
            ? orderA - orderB 
            : (a.nodeOrder || a.order || 999) - (b.nodeOrder || b.order || 999);
        });
      }
      
      // For other subAssetTypes: Use original order
      return propertyList.toSorted((a, b) => (a.nodeOrder || a.order || 999) - (b.nodeOrder || b.order || 999));
    }, [propertyList, openAsset?.subAssetType, properties]);

    const getPropertyName = (property) => {
        const inputObj = property?.labelTranslation.find(
          (e) => e.language === scope.projectLanguageCode
        );
        return getVariantText(inputObj.title || property?.name, "body1", {
          fontWeight: "bold",
          color: "#254a9a",
          margin: 5,
          textTransform: "uppercase",
        });
      };

  return (
    <>
       {sortedItemList.map((item, index) => {
              return (
                <tr
                  key={item.nodeIdent}
                  id={item.nodeIdent}
                >
                  <td
                    id={`text-${item.nodeIdent}`}
                    style={{
                      wordWrap: "break-word",
                      width: "25%",
                      fontSize: "12px",
                      verticalAlign: "baseline",
                    }}
                  >
                    <div className="sticky-text p-1"
                    >
                    {getPropertyName(item)}
                    </div>
                  </td>
                  <td style={{ width: "75%" }}>
                    <List className={"w-100 p-0"}>
                      <RenderNodeValues item={item} 
                      onUpdate={(item, nodeValue) =>{
                        updatePropertyList(item, nodeValue)
                      }}/>
                    </List>
                  </td>
                </tr>
              );
            })}
            </>
  )
}

export default RenderProperty